import React, { useState, useEffect } from 'react';
import { gql, useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Fade from '@material-ui/core/Fade';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Alert from '@material-ui/lab/Alert';
import { useCookies } from 'react-cookie';
import {
  GET_USER_CONFIG,
  GET_USER_CONFIG_ID,
  ADD_USER_CONFIG,
  UPDATE_USER_CONFIG,
  REMOVE_USER_CONFIG,
} from '../util/schema';

import AnimatedEllipsis from '../components/AnimatedEllipsis.jsx';
import buildings from '../icons/buildings.svg';
import buildingsDark from '../icons/buildings_dark.svg';
import logo from '../icons/premisys_logo.svg';
import logoDark from '../icons/premisys_logo_dark.svg';
// import pdcLogo from '../icons/pdc_identicard.svg';
// import pdcLogoDark from '../icons/pdc_identicard_dark.svg';
import getNameFromToken from '../util/jwtToken';

const LOGIN = gql`
  mutation login($input: LoginInput!) {
    login(input: $input)
  }
`;

const useStyles = makeStyles((theme) => ({
  background: {
    height: '100vh',
    [theme.breakpoints.up('sm')]: {
      backgroundImage: `url(${theme.palette.type === 'dark' ? buildingsDark : buildings})`,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
    },
  },
  // pdcLogo: {
  //   position: 'absolute',
  //   left: 0,
  //   top: 0,
  //   maxWidth: theme.spacing(35),
  //   [theme.breakpoints.down('lg')]: {
  //     maxWidth: theme.spacing(25),
  //   },
  //   [theme.breakpoints.down('md')]: {
  //     display: 'none',
  //   },
  // },
  paper: {
    height: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  flexGrow: {
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
      flexGrow: 0.4,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
  },
  logo: {
    width: theme.spacing(60),
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(48),
      maxWidth: '100%',
    },
    marginBottom: theme.spacing(3),
  },
  errorBar: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(-3),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '100%',
  },
  formContent: {
    display: 'flex',
    flexDirection: 'column',
    width: theme.spacing(58),
    [theme.breakpoints.down('sm')]: {
      width: '80%',
    },
  },
  textField: {
    marginTop: theme.spacing(5),
    [`& fieldset`]: {
      borderRadius: 0,
    },
  },
  forgot: {
    fontSize: theme.spacing(1.6),
    alignSelf: 'flex-end',
    marginBottom: theme.spacing(4),
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  helperText: {
    position: 'absolute',
    top: theme.spacing(6.5),
    left: theme.spacing(-1),
  },
  otherOptions: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
    },
  },
  grid: {
    width: theme.spacing(58),
    [theme.breakpoints.down('sm')]: {
      maxWidth: '80%',
    },
  },
  button: {
    height: '2.8em',
    [theme.breakpoints.down('sm')]: {
      height: '2.5em',
    },
    width: '100%',
    fontSize: '1em',
  },
  footer: {
    display: 'flex',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  footerText: {
    fontSize: theme.spacing(1.6),
  },
  spacer: {
    width: theme.spacing(2),
  },
  statusContainer: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  statusPaper: {
    width: theme.spacing(105),
    height: theme.spacing(70),
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      height: '100vh',
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fade: {
    position: 'absolute',
    width: theme.spacing(100),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    height: '100vh',
    zIndex: 2,
    left: '50%',
    transform: 'translate(-50%)',
  },
  statusFade: {
    position: 'absolute',
    zIndex: 2,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  signInArea: {
    display: 'flex',
    marginTop: theme.spacing(3),
  },
  signInText: {
    fontSize: '1.5em',
    color: theme.palette.header.main,
  },
  dialogTitle: {
    textAlign: 'center',
  },
}));

const LoginPage = ({ setApiToken, apiToken, setMode, setFilters, ...props }) => {
  const classes = useStyles();
  const theme = useTheme();
  const preventDefault = (event) => event.preventDefault();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [showStatus, setShowStatus] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(false);
  const [welcomemsg, setMsg] = useState('Welcome! Please sign in to your account.');
  const [cookies, setCookie, removeCookie] = useCookies(['name']);
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const queryUserConfig = (data) => {
    if (data == null) {
      setMode(false);
    } else {
      const obj = JSON.parse(data.userConfigData.Settings);
      if (obj.Value.filters) {
        try {
          const filters = JSON.parse(obj.Value.filters);
          setFilters(filters);
        } catch (err) {
          console.log('error', err);
        }
      }
      if (obj.Value.darkmode) {
        try {
          const thismode = JSON.parse(obj.Value.darkmode);
          setMode(thismode);
        } catch (err) {
          console.log('error', err);
        }
      }
      return obj;
    }
  };

  const queryErrorUserConfig = ({ graphQLErrors, networkError }) => {
    console.log(graphQLErrors);
    console.log(networkError);
  };

  const [
    fetchUserConfig,
    { data: userData, loading: userDataLoading, error: userDataError, fetchMore },
  ] = useLazyQuery(GET_USER_CONFIG, {
    notifyOnNetworkStatusChange: true,
    onCompleted: queryUserConfig,
    onError: queryErrorUserConfig,
  });

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const loginWithToken = (token) => {
    setTempToken(token);
    fetchUserConfig({
      variables: { userconfigName: getNameFromToken(token) },
      context: { headers: { authorization: `Bearer ${token}` } },
    });
    setTimeout(() => setApiToken(`Bearer ${token}`), 2000);
  };

  const loginCompleted = (data) => {
    if (data?.login) {
      setTimeout(() => loginWithToken(data.login), 1000);
    }
  };

  const loginErrored = (error) => {
    setTimeout(() => setShowStatus(false), 200);
    setTimeout(() => setShowForm(true), 400);
    if (error.networkError) {
      setServerError('Error connecting to server.');
      return;
    }
    if (error.graphQLErrors) {
      error.graphQLErrors.forEach(({ extensions }) => {
        if (extensions && extensions.code === 'INVALID_CREDENTIALS') {
          setUsernameError(null);
          setPasswordError(null);
          setUsername('');
          setPassword('');
          setServerError('Invalid username or password.');
        }
      });
    }
  };

  const [login, { error }] = useMutation(LOGIN, {
    errorPolicy: 'all',
    onCompleted: loginCompleted,
    onError: () => {}, // Suppress network error page. Handled in loginErrored function
  });

  React.useEffect(() => {
    if (error) {
      loginErrored(error);
    }
    if (cookies.Checkbox && !error) {
      setState(cookies.Checkbox);
      setUsername(cookies.username);
      setCookie('Checkbox', !state, { sameSite: 'strict' });
      if (cookies.username) {
        setMsg('Welcome back..... Please enter your password to continue');
      }
    }
  }, [error]);

  const handleLogin = (e) => {
    e.preventDefault();
    setUsernameError(!username || username.length === 0 ? 'Username required.' : null);
    setPasswordError(!password || password.length === 0 ? 'Password required.' : null);
    if (!username || username.length === 0 || !password || password.length === 0) {
      return;
    }

    setShowForm(false);
    setTimeout(() => setShowStatus(true), 200);
    login({ variables: { input: { username, password } } });
    if (state && !error) {
      setCookie('username', username, { sameSite: 'strict' });
      setCookie('Checkbox', state, { sameSite: 'strict' });
    } else {
      removeCookie('username', username);
      removeCookie('Checkbox', state);
    }
    sessionStorage.setItem('activeSession', 'true');
  };

  const handleChange = () => {
    setState(!state);
  };

  return (
    <div className={classes.background}>
      {/* <img
        className={classes.pdcLogo}
        src={theme.palette.type === 'dark' ? pdcLogoDark : pdcLogo}
        alt="PDC IDenticard"
      /> */}
      <Fade in={showForm} className={classes.fade}>
        <Paper className={classes.paper} square>
          <div className={classes.content}>
            <div className={classes.flexGrow} />
            <img
              className={classes.logo}
              src={theme.palette.type === 'dark' ? logoDark : logo}
              alt="PremiSys"
            />
            <Typography align="center" style={{ color: theme.palette.neutral.light }}>
              {welcomemsg}
            </Typography>
            <Collapse in={serverError != null}>
              <Alert
                severity="error"
                className={classes.errorBar}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setServerError(null);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {serverError}
              </Alert>
            </Collapse>
            <form className={classes.form} onSubmit={handleLogin} noValidate autoComplete="off">
              <div className={classes.formContent}>
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  autoFocus
                  id="username"
                  label="Username"
                  value={username}
                  error={usernameError != null}
                  helperText={usernameError}
                  FormHelperTextProps={{ className: classes.helperText }}
                  onChange={(e) => setUsername(e.target.value)}
                  onFocus={() => setUsernameError(null)}
                />
                <TextField
                  className={classes.textField}
                  variant="outlined"
                  id="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  error={passwordError != null}
                  helperText={passwordError}
                  FormHelperTextProps={{ className: classes.helperText }}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordError(null)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography className={classes.forgot}>
                  {/* TODO: add link destination or convert to button  */}
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link href="#" onClick={handleClickOpen}>
                    Forgot Password?
                  </Link>
                </Typography>
                <div>
                  <Dialog
                    onClose={handleClose}
                    aria-labelledby="customized-dialog-title"
                    open={open}
                  >
                    <DialogTitle
                      id="customized-dialog-title"
                      onClose={handleClose}
                      className={classes.dialogTitle}
                    >
                      Forgot Password
                    </DialogTitle>
                    <DialogContent dividers>
                      <Typography>
                        Please contact your system administrator if you are unable to sign in to
                        your account
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        autoFocus
                        onClick={handleClose}
                        color="secondary"
                        type="submit"
                        variant="contained"
                      >
                        Return to Sign In
                      </Button>
                    </DialogActions>
                  </Dialog>
                </div>
              </div>
              <Grid container className={classes.grid}>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="rememberMe"
                        defaultValue={false}
                        checked={state}
                        onInput={handleChange}
                      />
                    }
                    label="Remember me"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Button
                    className={classes.button}
                    type="submit"
                    variant="contained"
                    color="secondary"
                  >
                    Sign In
                  </Button>
                </Grid>
              </Grid>
            </form>
            {/* <Typography className={classes.otherOptions}>Other Sign In Options</Typography>
            <div className={classes.form}>
              <Button
                className={classes.button}
                variant="contained"
                startIcon={<MicrosoftIcon />}
                style={{ backgroundColor: '#3c80f6', color: '#fff', margin: theme.spacing(0.5) }}
              >
                Sign In with Microsoft
              </Button>
              <Button
                className={classes.button}
                variant="contained"
                startIcon={<GoogleIcon />}
                style={{ backgroundColor: '#e12020', color: '#fff', margin: theme.spacing(0.5) }}
              >
                Sign In with Google
              </Button>
              <Button
                className={classes.button}
                variant="contained"
                startIcon={<OktaIcon />}
                style={{ backgroundColor: '#3c80f6', color: '#fff', margin: theme.spacing(0.5) }}
              >
                Sign In with Okta
              </Button>
              <Button
                className={classes.button}
                variant="contained"
                style={{ backgroundColor: '#fff', margin: theme.spacing(0.5) }}
              >
                Sign In with Other Providers
              </Button>
            </div> */}
            <div className={classes.flexGrow} />
            <div className={classes.footer}>
              <Typography className={classes.footerText}>
                {/* TODO: add link destination or convert to button  */}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link href="#" onClick={preventDefault}>
                  Terms of use
                </Link>
              </Typography>
              <div className={classes.spacer} />
              <Typography className={classes.footerText}>
                {/* TODO: add link destination or convert to button  */}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link href="#" onClick={preventDefault}>
                  Privacy policy
                </Link>
              </Typography>
            </div>
          </div>
        </Paper>
      </Fade>
      <Fade in={showStatus} className={classes.statusFade}>
        <div className={classes.statusContainer}>
          <Paper className={classes.statusPaper} square>
            <Fade in={tempToken != null}>
              <Typography
                align="center"
                style={{ fontSize: theme.spacing(5), color: theme.palette.header.main }}
              >
                Welcome, <b>{getNameFromToken(tempToken)}</b>
              </Typography>
            </Fade>
            <div className={classes.signInArea}>
              <Typography className={classes.signInText}>Signing in</Typography>
              <AnimatedEllipsis className={classes.signInText} />
            </div>
          </Paper>
        </div>
      </Fade>
    </div>
  );
};

export default LoginPage;
